// Growth OS date range — owner, 2026-11-01. Every card, chart and the funnel
// read the same range; it lives in the URL so a refresh or a shared link opens
// on it. Dates are Riyadh calendar dates (UTC+3 all year, no daylight saving),
// so a day starts at 00:00 Riyadh whatever the viewer's clock says.
//
// URL: ?r=today | yesterday | 7 | 30 | 90 | custom (&from=YYYY-MM-DD&to=YYYY-MM-DD)
//      &cmp=0 turns the comparison off (it is on by default).

const DAY = 864e5;
const RIYADH_OFFSET = '+03:00';
export const DEFAULT_PRESET = '7';
export const PRESETS = [
  { key: 'today', ar: 'اليوم' },
  { key: 'yesterday', ar: 'أمس' },
  { key: '7', ar: 'آخر 7 أيام' },
  { key: '30', ar: 'آخر 30 يوماً' },
  { key: '90', ar: 'آخر 90 يوماً' },
];

// Below this many in a period, a percentage is shown greyed with a note, not as
// a finding. Owner's example; 30 is where a conversion rate stops swinging by
// whole tens of points on a single visitor.
export const MIN_SAMPLE = 30;

const dayFmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Riyadh' });
export const riyadhDay = (ms = Date.now()) => dayFmt.format(new Date(ms));
export const addDays = (ymd, n) => new Date(Date.parse(`${ymd}T00:00:00Z`) + n * DAY).toISOString().slice(0, 10);
export const dayStart = (ymd) => Date.parse(`${ymd}T00:00:00${RIYADH_OFFSET}`);
const isYmd = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(Date.parse(`${s}T00:00:00Z`));

// URL → range. Anything unreadable falls back to the default, never to an error.
export function readRange(params, now = Date.now()) {
  const today = riyadhDay(now);
  const r = params.get('r') || DEFAULT_PRESET;
  const compare = params.get('cmp') !== '0';
  if (r === 'custom') {
    let from = params.get('from'); let to = params.get('to');
    if (isYmd(from) && isYmd(to)) {
      if (from > to) [from, to] = [to, from];
      if (to > today) to = today;
      if (from > today) from = today;
      return { preset: 'custom', from, to, compare };
    }
  }
  if (r === 'today') return { preset: 'today', from: today, to: today, compare };
  if (r === 'yesterday') { const y = addDays(today, -1); return { preset: 'yesterday', from: y, to: y, compare }; }
  const n = ['7', '30', '90'].includes(r) ? Number(r) : Number(DEFAULT_PRESET);
  return { preset: String(n), from: addDays(today, -(n - 1)), to: today, compare };
}

// range → URL params (the default preset and comparison-on are left out).
export function writeRange(range) {
  const p = new URLSearchParams();
  if (range.preset !== DEFAULT_PRESET) p.set('r', range.preset);
  if (range.preset === 'custom') { p.set('from', range.from); p.set('to', range.to); }
  if (!range.compare) p.set('cmp', '0');
  return p;
}

// range → the instants the data is cut on: [start, end) and the period before it.
export function rangeWindow(range) {
  const start = dayStart(range.from);
  const end = dayStart(addDays(range.to, 1));
  const days = Math.round((end - start) / DAY);
  return { start, end, prevStart: start - (end - start), days, hourly: days === 1, from: range.from, to: range.to };
}

const monthDay = new Intl.DateTimeFormat('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', timeZone: 'UTC' });
const monthYear = new Intl.DateTimeFormat('ar-SA-u-ca-gregory-nu-latn', { month: 'long', year: 'numeric', timeZone: 'UTC' });
export const fmtDay = (ymd) => monthDay.format(new Date(`${ymd}T00:00:00Z`));
export const fmtMonth = (ym) => monthYear.format(new Date(`${ym}-01T00:00:00Z`));

// «من … إلى …» in words: a dash between two dates is reordered by the
// bidi algorithm on an RTL page and reads backwards.
export const spanLabel = (from, to) => (from === to ? fmtDay(from) : `${fmtDay(from)} إلى ${fmtDay(to)}`);

export function rangeLabel(range) {
  const pre = PRESETS.find((p) => p.key === range.preset);
  return pre ? pre.ar : spanLabel(range.from, range.to);
}

// A month's grid for the calendar: weeks start on Sunday (Saudi week).
export function monthGrid(ym) {
  const first = new Date(`${ym}-01T00:00:00Z`);
  const lead = first.getUTCDay();
  const len = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  const cells = Array.from({ length: lead }, () => null);
  for (let d = 1; d <= len; d++) cells.push(`${ym}-${String(d).padStart(2, '0')}`);
  while (cells.length % 7) cells.push(null);
  return cells;
}
export const shiftMonth = (ym, n) => {
  const d = new Date(`${ym}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + n);
  return d.toISOString().slice(0, 7);
};

// «1 نوفمبر، 10:27 م» — a moment in Riyadh time, for every «آخر ظهور» and
// event time (owner, 2026-11-01). The year is added only when it is not this year.
const atParts = new Intl.DateTimeFormat('ar-SA-u-ca-gregory-nu-latn', {
  timeZone: 'Asia/Riyadh', day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
});
export function fmtAt(at, now = Date.now()) {
  const ms = typeof at === 'number' ? at : Date.parse(at);
  if (!Number.isFinite(ms)) return '—';
  const p = Object.fromEntries(atParts.formatToParts(new Date(ms)).map((x) => [x.type, x.value]));
  const thisYear = Object.fromEntries(atParts.formatToParts(new Date(now)).map((x) => [x.type, x.value])).year;
  const date = p.year === thisYear ? `${p.day} ${p.month}` : `${p.day} ${p.month} ${p.year}`;
  return `${date}، ${p.hour}:${p.minute} ${p.dayPeriod}`;
}
